import { SelectValue } from "antd/lib/select";
import { useEffect, useState } from "react"
import { useIntl } from "react-intl";
import { useParams } from "react-router";

import { useAuthenticationDataContext } from "../../Helpers/AuthenticationDataProvider";
import { useIntlWrapperContext } from "../../Helpers/IntlWrapperProvider";
import useFetchWrapper, { HttpResponse } from "../../Helpers/useFetchWrapper";
import useScoreHelper from '../../Helpers/useScoreHelper';

import { Rating, RatingResponse, User, UserCardData } from "./BackofficeRating.model"
import moment from "moment";

export const useBackofficeRating = () => {
	const [admins, setAdmins] = useState<User[]>([]);
	const [loading, setLoading] = useState(true);
	const [rating, setRating] = useState<Rating>();
	const [user, setUser] = useState<UserCardData>();

	const { get, put } = useFetchWrapper();

	const { userId, userRatedId } = useParams<{ userId: string, userRatedId: string }>();

	const { getI18nLanguageName } = useIntlWrapperContext();
	const { getScoreByAverageRating, getScoreByRating } = useScoreHelper();

	const { userId: adminId } = useAuthenticationDataContext();

	const intl = useIntl();

	const isAssigned = () => {
		return adminId === rating?.assignedTo;
	}

	const approveReview = async () => {
		await put(`/review/rating-resolve/${rating?.user.userId}/${rating?.userRated.userId}`, { status: 'Accepted' });
		getRating();
	}

	const rejectReview = async () => {
		await put(`/review/rating-resolve/${rating?.user.userId}/${rating?.userRated.userId}`, { status: 'Rejected' });
		getRating();
	}

	const assignRating = async (value: SelectValue, rating: Rating) => {
		await put(`/review/rating-assign/${rating.user.userId}/${rating.userRated.userId}`, { userId: value });
		getRating();
	}

	const reassignRating = async (value: SelectValue, rating: Rating) => {
		await put(`/review/rating-reassign/${rating.user.userId}/${rating.userRated.userId}`, { userId: value });
		getRating();
	}

	const formatDate = (date: string) => {
		return moment(date).format("DD MMM YYYY");
	}

	const getAdmins = async () => {
		const response: HttpResponse<any> = await get(`/user/admin?page=${1}&size=${100}`);
		if (response.parsedBody) {
			const { users } = response.parsedBody;
			setAdmins(users);
		}
	}

	const getRating = async () => {
		!loading && setLoading(true);

		const response: HttpResponse<RatingResponse> = await get(`/review/rating/${userId}/${userRatedId}`);
		if (response.parsedBody) {
			setRating({
				...response.parsedBody,
				score: getScoreByRating(response.parsedBody.score),
			});
			await getUser(response.parsedBody.userRated.userId);
		}

		setLoading(false);
	}

	const getUser = async (id: string) => {

		const response: HttpResponse<any> = await get(`/user/profile/${id}`);
		
		if (response.parsedBody) {
			const {
				averageScore,
				currentPosition,
				education,
				firstName,
				imageId,
				isDeleted,
				languages,
				lastName,
				location,
				pronouns,
				researchAreas,
			} = response.parsedBody;

			const user: UserCardData = {
				averageScore: getScoreByAverageRating(averageScore),
				currentPosition,
				education,
				imageId,
				isDeleted,
				languages: languages?.map((code: string) => getI18nLanguageName(code)),
				location: location,
				name: !isDeleted ? `${firstName} ${lastName}` :  intl.formatMessage({id: "userLabels.deletedUser"}),
				pronouns,
				researchAreas,
			};
			
			setUser(user);
		}
	}

	const isAssignedAdminDeleted = (adminId: string) => {
		return !admins?.find(admin => admin.userId === adminId);
	}

	useEffect(() => {
		getAdmins();
		getRating();
	}, []);

	return {
		approveReview,
		assignRating,
		formatDate,
		isAssigned,
		isAssignedAdminDeleted,
		reassignRating,
		rejectReview,
		state: {
			admins,
			loading,
			rating,
			user
		}

	}
}